import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import AuthService from "../../../api/services/auth.service";
import { setCookie } from "../../../utils/cookies";
import { useCurrencies } from "../../../context/CurrenciesContext";
import { useTranslation } from "react-i18next";
import { getRedirectURI } from "../../../utils/googleOAuth";
import i18n from "../../../i18n";
import styles from "./GoogleCallback.module.scss";

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setIsLoggedIn, setEmail, setUser } = useAuth();
  const { currencyId } = useCurrencies();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const { t, ready } = useTranslation("translation");
  const hasProcessedRef = useRef(false); // Prevent duplicate processing

  // Initialize language from localStorage if not set (callback route has no lang prefix)
  useEffect(() => {
    if (!i18n.isInitialized) {
      return;
    }
    const savedLang = localStorage.getItem("i18nextLng") || "az";
    if (i18n.language !== savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  useEffect(() => {
    // Wait for i18n to be ready before processing
    if (!ready || !i18n.isInitialized) {
      return;
    }

    // Prevent multiple executions (React Strict Mode or re-renders)
    if (hasProcessedRef.current) {
      return;
    }

    const handleGoogleCallback = async () => {
      const code = searchParams.get("code");
      const errorParam = searchParams.get("error");

      if (errorParam) {
        hasProcessedRef.current = true;
        setError(t("google_oauth.error") || "Authentication failed");
        setLoading(false);
        setTimeout(() => {
          navigate("/auth/login");
        }, 3000);
        return;
      }

      if (!code) {
        hasProcessedRef.current = true;
        setError(t("google_oauth.no_code") || "No authorization code received");
        setLoading(false);
        setTimeout(() => {
          navigate("/auth/login");
        }, 3000);
        return;
      }

      // Mark as processing to prevent duplicate calls
      hasProcessedRef.current = true;

      try {
        // Get the redirect URI that was used (must match what was sent to Google)
        const redirectURI = getRedirectURI();

        // Send code and redirect_uri to backend
        const response = await AuthService.loginGoogle(code, redirectURI);

        if (response.data.access_token) {
          // Save token
          setCookie("LuxMartClientAccessToken", response.data.access_token, 7);

          // Update auth context
          if (response.data.user) {
            setEmail(response.data.user.email);
            setUser(response.data.user);
          }
          setIsLoggedIn(true);

          // Sync guest basket if exists
          const storedBasket = localStorage.getItem("guest_basket");
          if (storedBasket) {
            try {
              const basketData = JSON.parse(storedBasket);
              if (Array.isArray(basketData) && basketData.length > 0) {
                const jsonData = basketData.map(
                  ({ product_variant_id, quantity }: any) => ({
                    product_variant_id,
                    quantity,
                  }),
                );

                const formData = new FormData();
                formData.append("json_data", JSON.stringify(jsonData));

                await AuthService.setSavedBasket(Number(currencyId), formData);
                localStorage.removeItem("guest_basket");
              }
            } catch (basketError) {
              console.error("Error syncing guest basket:", basketError);
            }
          }

          // Redirect to home
          navigate("/");
        } else {
          throw new Error("No access token in response");
        }
      } catch (error: any) {
        console.error("Google OAuth error:", error);
        let message = t("google_oauth.error") || "Authentication failed";

        if (error.response?.data?.error) {
          message = error.response.data.error;
        } else if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.message) {
          message = error.message;
        }

        setError(message);
        setLoading(false);

        setTimeout(() => {
          navigate("/auth/login");
        }, 3000);
      }
    };

    handleGoogleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]); // Wait for translations to be ready

  return (
    <div className={styles.callback}>
      <div className={styles.container}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinnerWrapper}>
              <div className={styles.spinner}></div>
              <svg
                className={styles.googleIcon}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            </div>
            <h1 className={styles.title}>
              {t("google_oauth.processing_title") || "Signing you in"}
            </h1>
            <p className={styles.message}>
              {t("google_oauth.processing") ||
                "Please wait while we complete your authentication"}
            </p>
          </div>
        ) : error ? (
          <div className={styles.error}>
            <div className={styles.iconWrapper}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                  fill="#EF4444"
                />
              </svg>
            </div>
            <h2 className={styles.title}>
              {t("google_oauth.error_title") || "Authentication Error"}
            </h2>
            <p className={styles.message}>{error}</p>
            <p className={styles.redirect}>
              {t("google_oauth.redirecting") || "Redirecting to login..."}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GoogleCallback;
